/**
 * WatiChannelHealth — extracted Iter 156.9 from `SettingsWatiCard.jsx`.
 *
 * Renders the WATI "Auto-Sync & Status" block — connection indicator,
 * auto-sync interval, last-sync timestamp, and per-country (India + US)
 * channel-health rows (webhook + hot-poll). Purely presentational; takes
 * `watiStatus` as a single prop, owns no state of its own.
 */
import { RefreshCw, CheckCircle, Clock } from "lucide-react";

export default function WatiChannelHealth({ watiStatus }) {
  return (
    <div className="p-3 bg-[#F5F2EA] rounded-md space-y-3" data-testid="wati-channel-health">
      <div className="flex items-center gap-2">
        {watiStatus?.configured ? (
          <>
            <CheckCircle size={16} className="text-green-600" />
            <span className="text-sm font-medium text-[#1C1917]">WATI Connected</span>
          </>
        ) : (
          <>
            <div className="w-2 h-2 bg-yellow-500 rounded-full" />
            <span className="text-sm font-medium text-[#1C1917]">WATI Not Configured</span>
          </>
        )}
      </div>

      {watiStatus?.auto_sync_enabled && (
        <div className="flex items-center gap-2 text-sm text-[#57534E]">
          <RefreshCw size={14} className="text-green-600" />
          Auto-sync every {Math.round(watiStatus.auto_sync_interval_seconds / 60)} minutes
        </div>
      )}

      {watiStatus?.last_sync && (
        <div className="flex items-center gap-2 text-sm text-[#57534E]">
          <Clock size={14} />
          Last sync: {new Date(watiStatus.last_sync).toLocaleString("en-IN", {
            day: "numeric",
            month: "short",
            hour: "2-digit",
            minute: "2-digit"
          })}
          {watiStatus.last_sync_count > 0 && (
            <span className="text-green-600">
              (+{watiStatus.last_sync_count} new)
            </span>
          )}
        </div>
      )}

      {/* Per-country channel health: webhook + hot-poll for India and US */}
      {["India", "US"].map((country) => {
        const key = country === "India" ? "india" : "us";
        const webhook = watiStatus?.[`webhook_${key}`];
        const hot = watiStatus?.[`hot_sync_${key}`];
        const configured = country === "India" ? watiStatus?.configured : watiStatus?.us_configured;
        if (!configured) return null;
        const flag = country === "India" ? "🇮🇳" : "🇺🇸";
        return (
          <div
            key={country}
            className="pt-2 mt-2 border-t border-[#E7E5E4]"
            data-testid={`channel-health-${key}`}
          >
            <div className="text-sm font-medium text-[#1C1917] mb-1.5">{flag} WATI {country}</div>

            {/* Hot polling row */}
            <div className="flex items-start gap-2 text-sm text-[#57534E]">
              <div className={`w-2 h-2 rounded-full mt-1.5 ${
                hot?.is_live ? "bg-green-500 animate-pulse" : hot?.enabled ? "bg-amber-500" : "bg-gray-400"
              }`} />
              <div className="flex-1">
                <div className={`text-xs font-medium ${
                  hot?.is_live ? "text-green-700" : hot?.enabled ? "text-amber-700" : "text-gray-700"
                }`}>
                  API Polling: {hot?.is_live ? "LIVE" : hot?.enabled ? "starting…" : "disabled"}
                </div>
                {hot?.is_live && (
                  <div className="text-xs text-[#57534E]">
                    Top {hot.top_n} chats every {hot.interval_seconds}s
                    {" · "}last run {hot.seconds_since_last}s ago
                    {typeof hot.last_new_messages === "number" && hot.last_new_messages > 0 && (
                      <span className="text-green-600"> · +{hot.last_new_messages} new</span>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Webhook row */}
            <div className="flex items-start gap-2 text-sm text-[#57534E] mt-1">
              <div className={`w-2 h-2 rounded-full mt-1.5 ${
                webhook?.is_live ? "bg-green-500 animate-pulse" : webhook?.received ? "bg-amber-500" : "bg-red-500"
              }`} />
              <div className="flex-1">
                <div className={`text-xs font-medium ${
                  webhook?.is_live ? "text-green-700" : webhook?.received ? "text-amber-700" : "text-red-700"
                }`}>
                  Webhook: {webhook?.is_live ? "LIVE" : webhook?.received ? "idle" : "not receiving"}
                </div>
                <div className="text-xs text-[#57534E]">
                  {webhook?.received ? (
                    <>
                      {webhook.total_received || 0} hits
                      {webhook.seconds_since_last != null && (
                        <> · last {webhook.seconds_since_last < 60 ? `${webhook.seconds_since_last}s` : `${Math.round(webhook.seconds_since_last / 60)}m`} ago</>
                      )}
                    </>
                  ) : (
                    <>Paste the {country} URL above into your WATI dashboard</>
                  )}
                </div>
              </div>
            </div>
          </div>
        );
      })}

      <p className="text-xs text-[#57534E] pt-1 border-t border-[#E7E5E4]">
        Webhooks + polling run together. Whichever delivers first wins; duplicates are auto-suppressed.
      </p>
    </div>
  );
}
